import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  api, setViewingPatientId,
  persistViewingPatient, readPersistedViewingPatient,
  setAuthToken,
} from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingPatient, _setViewingPatient] = useState(() => readPersistedViewingPatient());

  const setViewingPatient = useCallback((patientSummary) => {
    _setViewingPatient(patientSummary);
    setViewingPatientId(patientSummary?.patient_id || null);
    persistViewingPatient(patientSummary);
  }, []);

  const login = useCallback(async (email, password, mfa_code) => {
    // Backend sets the HttpOnly cookie AND returns the token in the response body.
    // We ALSO stash the token in sessionStorage as a Bearer-auth fallback for
    // cross-site iframe / mobile browser contexts where third-party cookies
    // are blocked.
    const res = await api.post("/auth/login", { email, password, mfa_code });
    if (res.data?.token) setAuthToken(res.data.token);
    setUser(res.data.user);
    _setViewingPatient(null);
    setViewingPatientId(null);
    persistViewingPatient(null);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* even if backend logout errors, clear client state */ }
    setAuthToken(null);
    setUser(null);
    _setViewingPatient(null);
    setViewingPatientId(null);
    persistViewingPatient(null);
  }, []);

  // Boot: try to hydrate the user from cookie OR Bearer token via /auth/me
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        if (res.data.role === "provider") {
          const persisted = readPersistedViewingPatient();
          if (persisted?.patient_id) setViewingPatientId(persisted.patient_id);
        } else {
          _setViewingPatient(null);
          setViewingPatientId(null);
          persistViewingPatient(null);
        }
      } catch {
        // No valid session — clear the sessionStorage token too so we don't
        // send a stale/expired Bearer on every subsequent request.
        setAuthToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, viewingPatient, setViewingPatient }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Format FastAPI error detail (string | array | object) into a display string. */
export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}
